import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";
import { motion } from "framer-motion";
import { ScrollReveal } from "../components/ScrollReveal";
import { Shield, FileText, Lock, DollarSign, Scale, HelpCircle } from "lucide-react";

export default function TermsOfService() {
  useSEO({
    title: "Terms of Service // Platform Agreement & Usage Guidelines",
    description: "Read the terms and conditions governing the use of Supply Spot's autonomous logistics and procurement matching services."
  });

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Glow Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[140px] -z-10 pointer-events-none" />

      {/* HERO SECTION */}
      <section className="relative pt-36 pb-16 px-6 max-w-7xl mx-auto text-center flex flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider shadow-md"
        >
          <Scale className="w-3.5 h-3.5" />
          <span>LEGAL CENTER</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl font-bold tracking-tight text-text-primary font-title max-w-4xl"
        >
          Terms of Service
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-text-secondary text-xs sm:text-sm max-w-2xl leading-relaxed"
        >
          Effective Date: June 20, 2025 &nbsp;|&nbsp; Last Updated: June 20, 2025
        </motion.p>
      </section>

      {/* CONTENT SECTION */}
      <section className="px-6 pb-24 max-w-7xl mx-auto relative z-10">
        <ScrollReveal duration={0.8} y={40}>
          <div className="glass-panel p-8 sm:p-12 md:p-16 rounded-3xl border border-brand-orange/10 shadow-2xl flex flex-col gap-10 text-left">
            
            {/* Overview / Introduction */}
            <div className="flex flex-col gap-4 border-b border-border-theme pb-8">
              <h2 className="text-xl font-title font-bold text-text-primary flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-orange" />
                Terms of Service for Supply Spot Pvt Ltd
              </h2>
              <p className="text-text-secondary text-xs sm:text-sm leading-relaxed font-bold tracking-wider text-brand-amber">
                PLEASE READ THESE TERMS CAREFULLY. BY ACCESSING OR USING THE SERVICES, YOU AGREE TO ALL TERMS AND CONDITIONS HEREIN.
              </p>
              <p className="text-text-secondary text-sm leading-relaxed">
                By using the Services (including our autonomous logistics command center, procurement matching suite, and supplier onboarding platform) provided by Supply Spot Pvt Ltd (&ldquo;Supply Spot,&rdquo; &ldquo;we,&rdquo; or &ldquo;us&rdquo;), you consent to be legally bound by these Terms of Service (&ldquo;Terms&rdquo;).
              </p>
            </div>

            {/* Section I */}
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-amber" />
                I. General Terms and Acceptance
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">1. Parties to the Agreement</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    These Terms form a legal and binding agreement between you and Supply Spot Pvt Ltd (address: N8, Souvenir, Kalapatti Road, Coimbatore - 641014, India). You confirm you are at least 18 years of age and possess the full legal authority (as Admin, Procurement Officer, Supplier Representative, or Employee) to enter into and bind your organization to this agreement.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">2. Service Description</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    Supply Spot provides cloud-based logistics management, procurement matching, and supplier onboarding solutions on a subscription basis. Services are provided &ldquo;AS IS&rdquo; and are paid-subscription only. Supply Spot does not offer free trials; however, demonstrations may be available to prospective clients. Supply Spot reserves the sole right to update, modify, or discontinue any feature or component of the Services at any time without liability.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">3. User Obligations and Restricted Regions</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    You warrant that you will use the Services exclusively for internal, legitimate business purposes of your organization. There are currently no restricted geographical regions for the use of our Services.
                  </p>
                </div>
              </div>
            </div>

            {/* Section II */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Lock className="w-5 h-5 text-brand-gold" />
                II. Usage and Content
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">4. User-Generated Content</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    You may upload and submit content (documents, logistics files, comments, data, etc., &ldquo;User Content&rdquo;). You own your User Content. However, you grant Supply Spot a worldwide, perpetual, non-exclusive, royalty-free, sublicensable, and transferable license to use, host, store, copy, and display your User Content solely for the purpose of operating, maintaining, securing, and improving the Services.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">5. Responsibilities and Restrictions</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Data Responsibility:</strong> You are solely responsible for the accuracy, legality, quality, and content of all User Content provided under your account.</li>
                    <li><strong>Prohibited Use:</strong> You must not use the Services for any illegal activity, to transmit malicious software, or in a way that interferes with the integrity or performance of the platform.</li>
                    <li><strong>Immediate Termination Right:</strong> Supply Spot reserves the right to terminate access immediately and without prior notice for severe security violations, breaches of intellectual property rights, or any activity that poses an immediate threat to the platform or other users.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">6. Third-Party Integrations and Services</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    We may offer integrations with third-party applications. We are not responsible for the functionality, data security, or contractual terms of these third-party services. Your use is governed solely by their respective policies.
                  </p>
                </div>
              </div>
            </div>

            {/* Section III */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-brand-orange" />
                III. Payments, Billing, and Termination
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">7. Payment and Billing</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li>We accept Banking transactions like NEFT, RTGS. All fees are due in advance for the subscription term purchased.</li>
                    <li><strong>Non-Refundable Fees:</strong> All fees paid to Supply Spot are strictly non-refundable and are payments for access to the Services, regardless of your organization&apos;s usage or termination status.</li>
                    <li><strong>Renewal and Changes:</strong> Supply Spot may change the Service fees or subscription terms upon 30 days written notice. Specific terms governing auto-renewal (if applicable) and cancellation methods will be detailed in the Billing Policy and must be explicitly agreed upon at the time of purchase or renewal.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">8. Termination</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Suspension:</strong> Supply Spot may suspend your access temporarily for a suspected breach, illegal activity, or extended periods of inactivity (defined as 90 consecutive days without login). We will provide prior notice wherever reasonably possible.</li>
                    <li><strong>Right to Respond:</strong> You will be given a reasonable opportunity to respond to the concerns regarding the suspension by contacting us within the period specified in the notification.</li>
                    <li><strong>Termination by Supply Spot (for Cause):</strong> Supply Spot reserves the right to permanently terminate access if a breach is confirmed and not remedied, or if immediate termination is legally warranted. Upon termination by Supply Spot for cause, all prepaid fees are immediately forfeited.</li>
                    <li><strong>Termination by User:</strong> You may terminate your subscription according to Clause 7(iii). Upon any termination, your right to use the Services ends immediately, and our data retention policies apply.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section IV */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Scale className="w-5 h-5 text-brand-amber" />
                IV. Warranties, Liability, and Governing Law
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">9. Disclaimer of Warranty</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    The Services are provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE,&rdquo; without any warranty, express or implied. Supply Spot disclaims all warranties, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Services will be uninterrupted, error-free, or fully secure.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">10. Limitation of Liability (Liability Limitation Clause)</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    To the maximum extent permitted by Indian law, Supply Spot, its affiliates, or employees shall not be liable for any indirect, incidental, special, punitive, or consequential damages (including, but not limited to, lost profits or data loss) resulting from your use or inability to use the Services; unauthorized access to our servers; or third-party conduct related to the Services.
                  </p>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    <strong>Maximum Liability:</strong> In no event shall the aggregate total liability of Supply Spot exceed the total amount you paid us during the twelve (12) months immediately preceding the event giving rise to the claim.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">11. Governing Law and Jurisdiction</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    These Terms shall be governed and construed exclusively in accordance with the laws of India. Any disputes arising out of or relating to these Terms or the Services shall be subject to the exclusive jurisdiction of the competent courts located in Coimbatore, Tamil Nadu, India.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-brand-gold" />
                Contact Information
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                For legal or privacy queries, contact us via the designated contact email: <a href="mailto:support@supplyspot.ai" className="text-brand-orange hover:underline">support@supplyspot.ai</a>.
              </p>
            </div>

          </div>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
}
